import { Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs/operators';

/**
 * Applique les nouvelles versions déployées sans avoir à vider le cache.
 * Le service worker télécharge la nouvelle version en arrière-plan, puis on recharge la page
 * au démarrage ou quand l'utilisateur revient sur l'app.
 * Le rechargement ne touche pas au stockage (token, langue...).
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
    private updateReady = false;

    constructor(private swUpdate: SwUpdate) {}

    init(): void {
        if (!this.swUpdate.isEnabled) {
            return;
        }

        this.swUpdate.versionUpdates
            .pipe(
                filter(
                    (event): event is VersionReadyEvent =>
                        event.type === 'VERSION_READY'
                )
            )
            .subscribe(() => (this.updateReady = true));

        // Le cache du service worker est corrompu : seule une page neuve depuis le serveur s'en sort
        this.swUpdate.unrecoverable.subscribe(() => document.location.reload());

        // Au lancement, l'app a pu être servie depuis le cache : on bascule tout de suite
        // sur la nouvelle version si elle existe
        this.checkForUpdate().then((ready) => {
            if (ready) {
                document.location.reload();
            }
        });

        // Retour sur l'app : on applique la version prête, sinon on vérifie s'il y en a une
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState !== 'visible') {
                return;
            }

            if (this.updateReady) {
                document.location.reload();
            } else {
                this.checkForUpdate();
            }
        });
    }

    private checkForUpdate(): Promise<boolean> {
        return this.swUpdate.checkForUpdate().catch(() => false);
    }
}
